"use client";

import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { Button } from "@cmp-src/primitives/button";
import {
  WorkplaceRequestElement,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { IconTile } from "@gettactic/components/src/data-display/IconTile";
import { type IconValue } from "@gettactic/components/src/icons";
import { MapPin, X } from "lucide-react";
import type { ElementType, ReactNode } from "react";
import {
  DetailChip,
  DetailHeaderBar,
  DetailHeaderTitle
} from "@/features/workplace-requests/components/detail-modal/DetailHeader";

const parseIconValue = (value?: string | null): IconValue | null => {
  if (!value) return null;
  const separator = value.indexOf(":");
  if (separator === -1) return null;
  return { set: value.slice(0, separator), icon: value.slice(separator + 1) };
};

interface RequestDetailHeaderBarProps {
  request: WorkplaceRequestElement;
  selectedType?: WorkplaceRequestTypeElement;
  categoryColor?: string;
  isInMobileDrawer: boolean;
  onClose?: () => void;
  /** Share button (or null when the viewer can't share). */
  shareAction: ReactNode;
  /** Admin overflow menu (or null for non-admins). */
  adminActions: ReactNode;
  /** Scrolled state, see {@link DetailHeaderBar}. */
  condensed?: boolean;
}

/**
 * Fixed top bar of the request detail modal: request id + actions, on the shared
 * detail-modal header bar.
 */
export const RequestDetailHeaderBar = ({
  request,
  selectedType,
  categoryColor,
  isInMobileDrawer,
  onClose,
  shareAction,
  adminActions,
  condensed = false
}: RequestDetailHeaderBarProps) => (
  <DetailHeaderBar
    eyebrow={request.display_id ? `REQ-${request.display_id}` : "Request"}
    title={request.title}
    icon={
      <IconTile
        color={categoryColor}
        icon={parseIconValue(selectedType?.icon)}
        size="sm"
        bordered
        className="h-6 w-6 shrink-0"
      />
    }
    condensed={condensed}
    actions={
      <>
        {shareAction}
        {adminActions}
        {isInMobileDrawer && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </>
    }
  />
);

interface RequestDetailHeaderDetailsProps {
  request: WorkplaceRequestElement;
  selectedType?: WorkplaceRequestTypeElement;
  categoryColor?: string;
  officeName: string;
  submitterName: string;
  /** `H1` in page mode, `DialogTitle` in modal mode. */
  TitleTag: ElementType;
}

/**
 * The large title + requester/location chips. Rendered as the first block inside
 * the scroll body so it scrolls away naturally (the {@link RequestDetailHeaderBar}
 * takes over once it's out of view).
 */
export const RequestDetailHeaderDetails = ({
  request,
  selectedType,
  categoryColor,
  officeName,
  submitterName,
  TitleTag
}: RequestDetailHeaderDetailsProps) => (
  <DetailHeaderTitle
    icon={
      <IconTile
        color={categoryColor}
        icon={parseIconValue(selectedType?.icon)}
        size="md"
        bordered
        className="h-10 w-10"
      />
    }
    title={request.title}
    subtitle={request.type?.name}
    TitleTag={TitleTag}
  >
    <DetailChip className="py-1 pr-3 pl-1">
      <UserAvatar
        user={{
          name: submitterName,
          email: request.submitter_user?.email || "",
          profile_photo_url: request.submitter_user?.profile_photo_url || ""
        }}
        size="s"
        className="flex-shrink-0"
      />
      {submitterName}
    </DetailChip>
    <DetailChip>
      <MapPin className="h-4 w-4 text-neutral-400" />
      {officeName}
    </DetailChip>
  </DetailHeaderTitle>
);
